// @ts-nocheck
/**
 * Story player for the planner setup (vhp-planner docs/story-setup.md). The slides are the funnel's own screens
 * (sections/meal-planner.liquid); this file plays the frame around them (snippets/vhp-planner-story.liquid):
 * background media per slide, chapter bars, captions, tap zones, hold to pause, sound, the count-up on "Deine
 * Zahlen", the scratch foil on the gift slide, confetti and reaction bursts. assets/vhp-planner-funnel.js decides
 * which slide is on and what comes next; it calls render(view) on every render, view = null when the story is off:
 *
 *   {
 *     screen: 'pain',                 // funnel screen (data-vhp-screen)
 *     keys: ['answer_time'],          // media keys in order of preference (Story-Slide blocks, data-vhp-story-fig)
 *     images: [url, …],               // background from their dishes when no block matches (or keys is empty)
 *     imageStyle: 'collage',          // 'collage' (zooming grid) · 'glide' (dishes passing by) · 'blur' (one, blurred)
 *     canBack: true,                  // header back arrow (a step before this slide)
 *     chapter: { index: 0, slide: 4, count: 6 },
 *     parts: 1,                       // story parts inside this screen ([data-vhp-story-part]), one bar each
 *     passive: true,                  // moves on by itself when the media ends (else waits for an answer)
 *     duration: 8000,                 // passive slide (or part) without video: ms
 *     caption: 'Zeile 1\nZeile 2',    // default caption (a block caption wins)
 *     numbers: { kcal, protein },     // "Deine Zahlen"
 *   }
 *
 *   const player = window.vhpPlannerStory.create({ funnel, next, back, close, sound });
 *   player.render(view) · player.hold() · player.foil(canvas, { onDone }) · player.burst(el) · player.react(btn)
 *   player.reply(host, text, { onDone }) · player.say(text) · player.holdToCommit(btn, { onDone, onShort })
 *   player.morph(el, change, { width })  // size change animated (FLIP), e.g. a card whose content swaps
 *   player.warm(key)                      // start loading a slide's clips early (1.3b montage)
 */
(() => {
  const IMG_MS = 5000;
  const HOLD_MS = 220;
  const COUNT_MS = 1200;
  // Story v2 (docs/story-setup-v2.md): Sören's typing dots before a reply, reading speed of a reply.
  const REPLY_DOTS_MS = 450;
  const REPLY_CPS = 14;
  // Longest a passive slide waits for its text to be read (docs/UX_REVIEW.md #11).
  const READ_MAX = 15000;
  const COMMIT_MS = 1200;
  const SOUND_KEY = 'vhp_story_sound';
  const CONFETTI = ['#5fd38d', '#fff', '#f2c14e', '#1a7a43', '#ff8a65'];

  const motionOk = () => !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  // Save-Data or a slow connection: the poster instead of the clip.
  const slowNet = (() => {
    const c = navigator.connection;
    return Boolean(c && (c.saveData || /(^|-)2g$|^3g$/.test(String(c.effectiveType || ''))));
  })();
  const readSound = () => {
    try {
      return localStorage.getItem(SOUND_KEY) === '1';
    } catch (e) {
      return false;
    }
  };
  const writeSound = (on) => {
    try {
      localStorage.setItem(SOUND_KEY, on ? '1' : '0');
    } catch (e) {
      /* private mode */
    }
  };
  const buzz = (ms) => {
    try {
      navigator.vibrate?.(ms);
    } catch (e) {
      /* not supported */
    }
  };

  const create = ({ funnel, next, back, close, sound }) => {
    const root = funnel.querySelector('[data-vhp-story]');
    if (!root) return null;
    const media = root.querySelector('[data-vhp-story-media]');
    const bars = root.querySelector('[data-vhp-story-bars]');
    const chapterEl = root.querySelector('[data-vhp-story-chapter]');
    const muteBtn = root.querySelector('[data-vhp-story-mute]');
    const soundPill = root.querySelector('[data-vhp-story-sound]');
    const backBtn = root.querySelector('[data-vhp-story-back]');
    const chapters = String(root.dataset.chapters || '').split('|');
    // Two chapters when the recipe one is skipped (view.chapter.short): "Schritt 1 von 3" reads "Schritt 1 von 2", the
    // week "Schritt 2 von 2" (docs/PERSONA_ATTACK_PLAN.md 2.10).
    const chapterLabel = (view) => {
      const label = chapters[view.chapter.index] || '';
      return view.chapter.short && view.chapter.index !== 1 ? label.replace(/3/g, '2') : label;
    };
    const figs = Array.from(root.querySelectorAll('[data-vhp-story-fig]'));
    // Background built from their dishes (likes collage, planning, voices).
    const dyn = document.createElement('div');
    dyn.className = 'vhp-story__dyn';
    dyn.hidden = true;
    media?.append(dyn);

    let view = null;
    let slideKey = '';
    let items = []; // active figures (montage)
    let index = 0;
    let part = 0;
    let started = 0; // ms timestamp of the current timed item / part
    let elapsed = 0; // ms already played of the current timed item before a pause
    let paused = false;
    let held = false; // auto-advance stopped (the visitor touched something on a passive slide)
    let raf = 0;
    let soundOn = readSound();
    let holdTimer = 0;
    let holding = false;
    let countRaf = 0;
    // Reading floor: when the slide / part started, time spent paused, a finish waiting for the text.
    let readAt = 0;
    let readPausedAt = 0;
    let readPausedMs = 0;
    let waitRead = false;
    let readNeedAt = 0; // last time the text was measured (every 0.5 s: replies and bubbles still come in)
    let readNeedMs = 0;

    const screenEl = () => (view ? funnel.querySelector(`[data-vhp-screen="${view.screen}"]`) : null);
    const videoOf = (fig) => (slowNet ? null : fig?.querySelector('video') || null);
    const current = () => items[index] || null;
    const parts = () => Math.max(1, view?.parts || 1);

    // Reading floor (docs/UX_REVIEW.md #11): a passive slide (or part) never moves on by itself before its text could
    // be read at REPLY_CPS, whatever its clip or duration says; a clip that ends earlier holds its last frame. Taps
    // still move on at once. Not for timed montages (maxClip), whose text is one dish name.
    const startRead = () => {
      readAt = performance.now();
      readPausedMs = 0;
      readPausedAt = paused ? readAt : 0;
      waitRead = false;
      readNeedAt = 0;
    };
    const readText = () => {
      const el = screenEl();
      if (!el) return '';
      const host = parts() > 1 ? el.querySelector('[data-vhp-story-part].is-on') || el : el;
      return (host.innerText || '').replace(/\s+/g, ' ').trim();
    };
    const readNeed = () => {
      if (!view?.passive || view.maxClip) return 0;
      const now = performance.now();
      if (now - readNeedAt > 500) {
        readNeedAt = now;
        readNeedMs = Math.min(READ_MAX, (readText().length * 1000) / REPLY_CPS);
      }
      return readNeedMs;
    };
    const readLeft = () => {
      const need = readNeed();
      if (!need) return 0;
      return need - ((readPausedAt || performance.now()) - readAt - readPausedMs);
    };

    // ---------- media ----------

    const stopAll = () => {
      figs.forEach((fig) => {
        const video = fig.querySelector('video');
        if (video && !video.paused) video.pause();
      });
    };

    const playVideo = (video) => {
      video.muted = !soundOn;
      const run = video.play();
      if (run?.catch) {
        run.catch(() => {
          // A timed montage (1.3b) keeps its clock: a clip that won't play shows its poster for its time.
          const skip = () => {
            if (!view?.maxClip) advanceItem();
          };
          // Sound needs a gesture on some browsers: muted then, the pill stays.
          if (!video.muted) {
            video.muted = true;
            video.play().catch(skip);
          } else {
            skip();
          }
        });
      }
    };

    const showItem = (i) => {
      index = i;
      if (view?.partsFollowMedia && part !== i) {
        part = i;
        paintParts();
      }
      elapsed = 0;
      started = performance.now();
      items.forEach((fig, n) => {
        fig.classList.toggle('is-on', n === i);
        const video = fig.querySelector('video');
        if (video && n !== i) video.pause();
      });
      const video = videoOf(current());
      if (video) {
        if (video.preload !== 'auto') video.preload = 'auto';
        try {
          video.currentTime = 0;
        } catch (e) {
          /* not loaded yet */
        }
        // Montage of recipe clips (1.3b): clipFrom < 0 = seconds before the end (where the finished dish is),
        // 0..1 = a share of the clip.
        if (view?.clipFrom) {
          const seek = () => {
            try {
              const d = video.duration;
              // A start picked in the block ("Clip-Start") wins.
              const pick = Number(items[i]?.dataset.start);
              if (Number.isFinite(pick) && pick >= 0 && pick < d) video.currentTime = pick;
              else if (d > 4) video.currentTime = view.clipFrom < 0 ? Math.max(0, d + view.clipFrom) : Math.min(d * view.clipFrom, d - 2);
            } catch (e) {
              /* not seekable yet */
            }
          };
          if (video.readyState >= 1) seek();
          else video.addEventListener('loadedmetadata', seek, { once: true });
        }
        if (!paused) playVideo(video);
        // A montage cut gets its full time from when it really plays (up to 1 s to load), so a slow clip isn't
        // over before it moved.
        if (view?.maxClip) {
          const fig = items[i];
          video.addEventListener('playing', () => {
            if (current() === fig && performance.now() - started < 1000) {
              started = performance.now();
              elapsed = 0;
            }
          }, { once: true });
        }
      }
      // The next clip of a montage loads while this one plays (fully when the clips are short cuts).
      const after = videoOf(items[i + 1]);
      if (after && after.preload !== 'auto') after.preload = view?.maxClip ? 'auto' : 'metadata';
      paintChip();
    };

    let finishing = false;
    const finish = () => {
      if (finishing) return;
      finishing = true;
      window.setTimeout(() => {
        finishing = false;
      }, 300);
      next();
    };

    // The montage's next item, or the end of the slide (passive: next part / slide, else from the start).
    const advanceItem = () => {
      if (!view) return;
      if (index + 1 < items.length) {
        showItem(index + 1);
        return;
      }
      if (view.passive && !held) {
        if (readLeft() > 0) waitRead = true; // tick finishes once the text is read
        else finish();
        return;
      }
      if (items.length) showItem(0);
    };

    const onEnded = (event) => {
      if (!view || event.target !== videoOf(current())) return;
      advanceItem();
    };
    figs.forEach((fig) => fig.querySelector('video')?.addEventListener('ended', onEnded));

    const paintDyn = () => {
      const urls = (view.images || []).filter(Boolean);
      const on = !items.length && urls.length > 0;
      dyn.hidden = !on;
      if (!on) {
        dyn.replaceChildren();
        dyn.dataset.key = '';
        return;
      }
      const style = view.imageStyle || 'collage';
      const key = `${style}|${urls.join('|')}`;
      if (dyn.dataset.key === key) return;
      dyn.dataset.key = key;
      dyn.className = `vhp-story__dyn vhp-story__dyn--${style}`;
      const list = style === 'glide' ? urls.concat(urls) : style === 'blur' || style === 'ambient' ? urls.slice(0, 1) : style === 'parts' ? urls : urls.slice(0, 3);
      const track = document.createElement('div');
      track.className = 'vhp-story__dyn-track';
      track.style.setProperty('--n', String(urls.length));
      list.forEach((src, n) => {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.decoding = 'async';
        img.style.setProperty('--i', String(n));
        track.append(img);
      });
      dyn.replaceChildren(track);
      if (style === 'parts') track.querySelectorAll('img').forEach((img, n) => img.classList.toggle('is-on', n === part));
    };

    // ---------- parts (several story segments inside one screen, e.g. one voice each) ----------

    const paintParts = () => {
      const el = screenEl();
      if (!el) return;
      el.querySelectorAll('[data-vhp-story-part]').forEach((node, n) => {
        node.classList.toggle('is-on', n === part);
        node.hidden = n !== part;
      });
      // 'parts' background: the photo of the current step.
      if (view?.imageStyle === 'parts') {
        dyn.querySelectorAll('img').forEach((img, n) => img.classList.toggle('is-on', n === part));
      }
    };

    const nextPart = () => {
      if (part + 1 < parts()) {
        part += 1;
        elapsed = 0;
        started = performance.now();
        paintParts();
        paintBars();
        startRead();
        return true;
      }
      return false;
    };

    const prevPart = () => {
      if (part > 0) {
        part -= 1;
        elapsed = 0;
        started = performance.now();
        paintParts();
        paintBars();
        return true;
      }
      return false;
    };

    // ---------- progress ----------

    const itemProgress = () => {
      // maxClip: a fixed time per step (a long or slow-loading clip shows only its start).
      if (view?.maxClip) return Math.min(1, (elapsed + (paused ? 0 : performance.now() - started)) / view.maxClip);
      const video = videoOf(current());
      if (video && video.duration > 0 && Number.isFinite(video.duration)) {
        return Math.min(1, video.currentTime / video.duration);
      }
      // A slide without clip: the bar runs as long as its text needs (reading floor), not just its fixed duration.
      const total = Math.max(items.length && parts() === 1 && !view?.partsFollowMedia ? IMG_MS : view?.duration || IMG_MS, readNeed());
      const ms = elapsed + (paused ? 0 : performance.now() - started);
      return Math.min(1, ms / total);
    };

    const tick = () => {
      raf = 0;
      if (!view) return;
      const fill = bars?.querySelector('.is-now > i');
      if (waitRead && !paused && !held && readLeft() <= 0) {
        waitRead = false;
        finish();
      }
      if (view.passive) {
        const multi = parts() === 1 && items.length > 1;
        let p = multi ? (index + itemProgress()) / items.length : itemProgress();
        if (view.oneBar && parts() > 1) p = (part + itemProgress()) / parts();
        if (fill) fill.style.transform = `scaleX(${Math.min(1, p)})`;
        const timed = view.partsFollowMedia || view.maxClip ? !videoOf(current()) || Boolean(view.maxClip) : parts() > 1 || !videoOf(current());
        if (timed && !paused && !held && itemProgress() >= 1) {
          if (view.partsFollowMedia) advanceItem();
          else if (parts() > 1) {
            if (readLeft() <= 0 && !nextPart()) finish();
          } else if (items.length) advanceItem();
          else if (readLeft() <= 0) finish();
        }
      } else if (items.length > 1 && !videoOf(current()) && !paused && itemProgress() >= 1) {
        advanceItem(); // image montage behind a question
      }
      raf = requestAnimationFrame(tick);
    };

    // fill (deck): the first n bars full, like a progress count (likes towards a plan).
    let barsKey = '';
    const paintBars = () => {
      if (!bars || !view) return;
      const { count, fill } = view.chapter;
      const slide = view.chapter.slide + (view.oneBar ? 0 : part);
      barsKey = `${view.chapter.index}|${slide}|${count}|${fill}`;
      if (fill != null) {
        if (bars.childElementCount !== count) bars.replaceChildren(...Array.from({ length: count }, () => {
          const bar = document.createElement('span');
          bar.append(document.createElement('i'));
          return bar;
        }));
        Array.from(bars.children).forEach((bar, n) => {
          bar.className = n < fill ? 'is-done' : '';
          bar.firstElementChild.style.transform = n < fill ? 'scaleX(1)' : 'scaleX(0)';
        });
        if (chapterEl) chapterEl.textContent = chapterLabel(view);
        return;
      }
      if (bars.childElementCount !== count) {
        bars.replaceChildren(
          ...Array.from({ length: count }, () => {
            const bar = document.createElement('span');
            bar.append(document.createElement('i'));
            return bar;
          })
        );
      }
      Array.from(bars.children).forEach((bar, n) => {
        bar.className = n < slide ? 'is-done' : n === slide ? 'is-now' : '';
        const fill = bar.firstElementChild;
        if (n < slide) fill.style.transform = 'scaleX(1)';
        else if (n > slide) fill.style.transform = 'scaleX(0)';
        else fill.style.transform = view.passive ? 'scaleX(0)' : 'scaleX(1)';
      });
      if (chapterEl) chapterEl.textContent = chapterLabel(view);
    };

    // ---------- caption, chip, numbers ----------

    const paintCaption = () => {
      const el = screenEl()?.querySelector('[data-vhp-story-cap]');
      if (!el) return;
      // Several obstacles answered: Sören's thread answers each, so a block's caption (written for one) stays out.
      const multi = (view.chat?.me?.length || 0) > 1;
      const text = (multi && view.caption) || items.find((fig) => fig.dataset.caption)?.dataset.caption || view.caption || '';
      const key = view.chat ? `${(view.chat.me || []).join('/')}|${text}` : text;
      if (el.dataset.text === key) return;
      el.dataset.text = key;
      el.classList.toggle('is-dm', Boolean(view.chat));
      if (view.chat) {
        paintThread(el, text);
        return;
      }
      el.replaceChildren(
        ...text
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line, n) => {
            const span = document.createElement('span');
            span.textContent = line;
            span.style.setProperty('--n', String(n));
            return span;
          })
      );
      el.hidden = !text;
    };

    // Answer slide as a DM thread: their own answer on the right first, then Sören typing on the left, then his
    // lines as bubbles (grouped, his face at the last one). Timing lives in CSS (--d per bubble).
    const paintThread = (el, text) => {
      const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);
      // One group of Sören's bubbles (his face at the last one); typed = dots in the first bubble before the words.
      const group = (rows, { typed = false, ask = false } = {}) => {
        const them = document.createElement('div');
        them.className = ask ? 'vhp-dm__them vhp-dm__them--ask' : 'vhp-dm__them';
        const pic = face();
        if (pic) {
          pic.className = 'vhp-dm__face';
          them.append(pic);
        }
        const col = document.createElement('div');
        col.className = 'vhp-dm__col';
        rows.forEach((line, n) => {
          const msg = document.createElement('p');
          msg.className = 'vhp-dm__msg';
          const words = document.createElement('span');
          words.textContent = line;
          msg.append(words);
          if (typed && n === 0) {
            const dots = document.createElement('span');
            dots.className = 'vhp-dm__dots';
            dots.setAttribute('aria-hidden', 'true');
            dots.append(document.createElement('i'), document.createElement('i'), document.createElement('i'));
            msg.append(dots);
          }
          col.append(msg);
        });
        them.append(col);
        return them;
      };
      const thread = [];
      // His question from 1.4 first, then their answers (one bubble each, sent in quick succession), then his reply.
      if (view.chat.ask) thread.push(group([view.chat.ask], { ask: true }));
      // One answer keeps the CSS times. Several: theirs 0.45 s apart, Sören types after the last one and his lines
      // (opener + one per obstacle) come 1.6 s apart, the same base times as the CSS (with / without his question).
      const mine = view.chat.me || [];
      const multi = mine.length > 1;
      const base = view.chat.ask ? { me: 0.75, type: 1.35, words: 2.15, line: 3.25 } : { me: 0.2, type: 0.9, words: 1.7, line: 2.8 };
      const shift = Math.max(0, mine.length - 1) * 0.45;
      const at = (el, s) => {
        if (el && multi) el.style.animationDelay = `${s.toFixed(2)}s`;
      };
      if (mine.length) {
        const box = document.createElement('div');
        box.className = 'vhp-dm__mine';
        mine.forEach((label, n) => {
          const me = document.createElement('p');
          me.className = 'vhp-dm__msg vhp-dm__msg--me';
          me.textContent = label;
          at(me, base.me + n * 0.45);
          box.append(me);
        });
        thread.push(box);
      }
      const reply = group(lines, { typed: true });
      at(reply.querySelector('.vhp-dm__face'), base.type + shift);
      at(reply.querySelector('.vhp-dm__dots'), base.type + shift);
      reply.querySelectorAll('.vhp-dm__col > .vhp-dm__msg').forEach((msg, n) => {
        at(msg, n === 0 ? base.type + shift : base.line + shift + (n - 1) * 1.6);
      });
      at(reply.querySelector('.vhp-dm__col > .vhp-dm__msg:first-child > span:first-child'), base.words + shift);
      thread.push(reply);
      el.classList.toggle('has-ask', Boolean(view.chat.ask));
      el.replaceChildren(...thread);
      el.hidden = !text;
      // The toggle and the quote come in after Sören's last bubble (CSS: --dm-end).
      const end = Math.max(0, ...Array.from(el.querySelectorAll('.vhp-dm__msg'), (msg) => parseFloat(getComputedStyle(msg).animationDelay) || 0));
      el.parentElement?.style.setProperty('--dm-end', `${end.toFixed(2)}s`);
    };

    // Answer slide as a chat (Maxim 2026-10-20): the thread sits at the bottom and a bubble takes no room until its
    // turn (CSS), so each new one pushes the older ones up. This glides that push instead of letting it jump.
    const glide = (stack) => {
      if (!stack || !('ResizeObserver' in window)) return;
      let last = 0;
      new ResizeObserver(() => {
        const h = stack.offsetHeight;
        const grow = h - last;
        const shown = last > 0; // not the slide opening
        last = h;
        if (!shown || grow <= 0 || !motionOk()) return;
        const t = getComputedStyle(stack).transform;
        const now = t && t !== 'none' ? new DOMMatrix(t).m42 : 0;
        stack.style.transition = 'none';
        stack.style.transform = `translateY(${now + grow}px)`;
        void stack.offsetHeight;
        stack.style.transition = 'transform 0.34s cubic-bezier(0.2, 0.8, 0.2, 1)';
        stack.style.transform = '';
      }).observe(stack);
    };
    glide(funnel.querySelector('[data-vhp-screen="answer"] .vhp-story-stack'));

    const paintChip = () => {
      const chip = screenEl()?.querySelector('[data-vhp-story-chip]');
      if (!chip) return;
      const protein = current()?.dataset.protein;
      chip.hidden = !protein;
      if (protein) chip.textContent = `🔥 ${window.vhpT ? window.vhpT('planner.story_protein_chip', { n: protein }) : `${protein} g Protein`}`;
    };

    // ---------- confetti / reactions ----------

    // A short burst from an element (number reveal, scratched day). Pure decoration; nothing with reduced motion.
    const burst = (el, { count = 26, spread = 1 } = {}) => {
      if (!el || !motionOk()) return;
      const host = root;
      const box = el.getBoundingClientRect();
      if (!box.width) return; // the slide is gone already (tapped on during the count-up)
      const frame = funnel.getBoundingClientRect();
      const x = box.left - frame.left + box.width / 2;
      const y = box.top - frame.top + box.height / 2;
      for (let i = 0; i < count; i += 1) {
        const bit = document.createElement('i');
        bit.className = 'vhp-story__confetti';
        const angle = Math.random() * Math.PI * 2;
        const dist = (60 + Math.random() * 120) * spread;
        bit.style.left = `${x}px`;
        bit.style.top = `${y}px`;
        bit.style.background = CONFETTI[i % CONFETTI.length];
        bit.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
        bit.style.setProperty('--dy', `${Math.sin(angle) * dist - 40}px`);
        bit.style.setProperty('--r', `${Math.random() * 540 - 270}deg`);
        host.append(bit);
        window.setTimeout(() => bit.remove(), 1100);
      }
    };

    // Emoji reaction (answer slides): the emoji floats up from the button, like on IG.
    const react = (btn) => {
      buzz(8);
      // In the DM thread the reaction sticks to Sören's last bubble, like a DM reaction.
      const bubbles = screenEl()?.querySelectorAll('.vhp-dm__them:not(.vhp-dm__them--ask) .vhp-dm__col > .vhp-dm__msg');
      const last = bubbles?.[bubbles.length - 1];
      if (last && btn) {
        last.dataset.react = btn.textContent.trim();
        last.classList.remove('is-reacted');
        void last.offsetWidth;
        last.classList.add('is-reacted');
      }
      if (!btn || !motionOk()) return;
      const box = btn.getBoundingClientRect();
      const frame = funnel.getBoundingClientRect();
      for (let i = 0; i < 6; i += 1) {
        const fly = document.createElement('span');
        fly.className = 'vhp-story__float';
        fly.textContent = btn.textContent.trim();
        fly.style.left = `${box.left - frame.left + box.width / 2 + (Math.random() * 60 - 30)}px`;
        fly.style.top = `${box.top - frame.top}px`;
        fly.style.animationDelay = `${i * 70}ms`;
        root.append(fly);
        window.setTimeout(() => fly.remove(), 1500);
      }
    };

    let counted = '';
    const paintNumbers = () => {
      const el = screenEl();
      const nums = view.numbers;
      if (!el || !nums) return;
      const kcalEl = el.querySelector('[data-vhp-num-kcal]');
      const proteinEl = el.querySelector('[data-vhp-num-protein]');
      if (!kcalEl || !proteinEl) return;
      const fmt = (n) => Math.round(n).toLocaleString(document.documentElement.lang || 'de');
      const key = `${nums.kcal}|${nums.protein}`;
      // Count up once when the slide opens; ±50 afterwards just updates.
      if (counted === key || counted.startsWith('done') || !motionOk()) {
        cancelAnimationFrame(countRaf);
        kcalEl.textContent = fmt(nums.kcal);
        proteinEl.textContent = fmt(nums.protein);
        el.classList.add('is-counted');
        counted = counted || key;
        return;
      }
      counted = key;
      el.classList.remove('is-counted');
      const from = performance.now();
      const step = () => {
        const t = Math.min(1, (performance.now() - from) / COUNT_MS);
        const ease = 1 - (1 - t) ** 3;
        kcalEl.textContent = fmt(nums.kcal * ease);
        proteinEl.textContent = fmt(nums.protein * ease);
        if (t < 1) countRaf = requestAnimationFrame(step);
        else {
          el.classList.add('is-counted');
          counted = `done|${key}`;
          buzz(12);
          burst(kcalEl, { count: 22, spread: 0.9 });
        }
      };
      countRaf = requestAnimationFrame(step);
    };

    // ---------- sound ----------

    const paintSound = () => {
      const anyVideo = items.some((fig) => videoOf(fig));
      if (muteBtn) {
        muteBtn.hidden = !anyVideo;
        muteBtn.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
        muteBtn.setAttribute('aria-label', soundOn ? muteBtn.dataset.on : muteBtn.dataset.off);
        muteBtn.classList.toggle('is-on', soundOn);
      }
      if (soundPill) soundPill.hidden = soundOn || !anyVideo;
    };

    const setSound = (on) => {
      soundOn = on;
      writeSound(on);
      const video = videoOf(current());
      if (video) {
        video.muted = !on;
        if (on && video.paused && !paused) video.play().catch(() => {});
      }
      if (on) sound?.();
      paintSound();
    };

    // ---------- pause / hold ----------

    const setPaused = (on) => {
      if (paused === on) return;
      paused = on;
      root.classList.toggle('is-paused', on);
      const video = videoOf(current());
      if (on) {
        elapsed += performance.now() - started;
        readPausedAt = performance.now();
        if (video) video.pause();
        pauseReply();
      } else {
        started = performance.now();
        if (readPausedAt) readPausedMs += performance.now() - readPausedAt;
        readPausedAt = 0;
        if (video) playVideo(video);
        resumeReply();
      }
    };

    const nudge = () => {
      const target = screenEl()?.querySelector('.vhp-poll, .vhp-choices, .vhp-gift, .vhp-hold__btn, .vhp-screen__footer .vhp-btn');
      if (!target) return;
      target.classList.remove('is-nudged');
      void target.offsetWidth;
      target.classList.add('is-nudged');
    };

    const tap = (dir) => {
      if (!view || view.deck) return;
      // A reply on screen: right skips to what comes after it, left drops it.
      if (replyDone) {
        if (dir === 'back') dropReply();
        else {
          endReply();
          return;
        }
      }
      if (view.partsFollowMedia && items.length) {
        if (dir === 'back') {
          if (index > 0) showItem(index - 1);
          else back();
        } else if (index + 1 < items.length) showItem(index + 1);
        else finish();
        return;
      }
      if (dir === 'back') {
        if (!prevPart()) back();
      } else if (nextPart()) {
        /* next voice */
      } else if (view.passive) finish();
      else nudge();
    };

    root.querySelectorAll('[data-vhp-story-tap]').forEach((zone) => {
      zone.addEventListener('pointerdown', () => {
        holding = false;
        window.clearTimeout(holdTimer);
        holdTimer = window.setTimeout(() => {
          holding = true;
          setPaused(true);
        }, HOLD_MS);
      });
      const release = () => {
        window.clearTimeout(holdTimer);
        if (holding) setPaused(false);
      };
      zone.addEventListener('pointerup', release);
      zone.addEventListener('pointercancel', release);
      zone.addEventListener('pointerleave', release);
      zone.addEventListener('contextmenu', (event) => event.preventDefault());
      zone.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (holding) {
          holding = false;
          return;
        }
        tap(zone.dataset.vhpStoryTap);
      });
    });

    // A reply on screen: a tap on the slide skips it, unless it lands on another control (a different answer is a
    // change of mind and goes through). The answer they already picked counts as "weiter".
    funnel.addEventListener(
      'click',
      (event) => {
        if (!replyDone || !view || view.deck) return;
        const el = event.target;
        if (!(el instanceof Element) || !screenEl()?.contains(el)) return;
        const control = el.closest('button, a, input, label, select, textarea, [role="button"]');
        const picked = control?.matches('.is-on, [aria-pressed="true"], [aria-checked="true"]');
        if (control && !picked && !el.closest('.vhp-story-reply')) return;
        event.preventDefault();
        event.stopPropagation();
        endReply();
      },
      true
    );

    backBtn?.addEventListener('click', (event) => {
      event.stopPropagation();
      back();
    });
    muteBtn?.addEventListener('click', (event) => {
      event.stopPropagation();
      setSound(!soundOn);
    });
    soundPill?.addEventListener('click', (event) => {
      event.stopPropagation();
      setSound(true);
    });
    root.querySelector('[data-vhp-story-close]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      close();
    });

    document.addEventListener('keydown', (event) => {
      if (!view || view.deck || event.defaultPrevented || event.altKey || event.metaKey || event.ctrlKey) return;
      if (event.target.closest?.('input, textarea, select, [contenteditable="true"], dialog')) return;
      if (event.key === 'ArrowLeft') tap('back');
      else if (event.key === 'ArrowRight') tap('next');
      else if (event.key === ' ' && event.target === document.body) {
        event.preventDefault();
        setPaused(!paused);
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (!view) return;
      if (document.hidden) setPaused(true);
      else if (!holding) setPaused(false);
    });

    // ---------- render ----------

    const off = () => {
      if (!view && root.hidden) return;
      dropReply();
      view = null;
      slideKey = '';
      items = [];
      cancelAnimationFrame(raf);
      raf = 0;
      stopAll();
      figs.forEach((fig) => {
        fig.hidden = true;
        fig.classList.remove('is-on');
      });
      dyn.hidden = true;
      root.hidden = true;
      funnel.classList.remove('is-story');
      delete funnel.dataset.storyPos;
      delete funnel.dataset.storyScreen;
      document.documentElement.classList.remove('vhp-story-open');
    };

    const render = (nextView) => {
      if (!nextView) {
        off();
        return;
      }
      const key = `${nextView.screen}|${nextView.keys.join(',')}`;
      const fresh = key !== slideKey;
      view = nextView;
      root.hidden = false;
      funnel.classList.add('is-story');
      funnel.dataset.storyScreen = nextView.screen;
      root.classList.toggle('is-deck', Boolean(nextView.deck));
      if (backBtn) backBtn.hidden = !nextView.canBack;
      document.documentElement.classList.add('vhp-story-open');
      if (fresh) {
        slideKey = key;
        dropReply();
        held = false;
        paused = false;
        part = 0;
        root.classList.remove('is-paused');
        stopAll();
        const want = nextView.keys.find((k) => figs.some((fig) => fig.dataset.vhpStoryFig === k));
        items = want ? figs.filter((fig) => fig.dataset.vhpStoryFig === want) : [];
        figs.forEach((fig) => {
          fig.hidden = !items.includes(fig);
          fig.classList.remove('is-on');
        });
        funnel.dataset.storyPos = items[0]?.dataset.pos || nextView.pos || 'bottom';
        root.classList.toggle('has-media', items.length > 0);
        if (items.length) showItem(0);
        else started = performance.now();
        elapsed = 0;
        startRead();
        if (nextView.screen === 'numbers') counted = '';
        paintParts();
        paintBars();
        paintSound();
        if (!raf) raf = requestAnimationFrame(tick);
        // Coming back to a slide: its poll without the old reply.
        screenEl()?.querySelectorAll('.vhp-story-reply').forEach((el) => el.remove());
      }
      // Bars that change without a new slide (the deck's like count).
      const chapterKey = `${nextView.chapter.index}|${nextView.chapter.slide + part}|${nextView.chapter.count}|${nextView.chapter.fill}`;
      if (!fresh && chapterKey !== barsKey) paintBars();
      paintDyn();
      paintCaption();
      paintChip();
      if (nextView.screen === 'numbers') paintNumbers();
    };

    // ---------- size changes: shared with the planner (assets/vhp-ui-story.js) ----------

    const morph = (...args) => window.vhpUi.morph(...args);
    const growIn = (...args) => window.vhpUi.growIn(...args);

    // ---------- Sören's replies (story v2) ----------

    // DM-style reply inside the sticker that was just answered (host): his photo, typing dots, then the text, read
    // at ~16 characters a second plus 0.6 s (1.8 to 8 s; was capped at 2.4 s, docs/UX_REVIEW.md #11; maxRead caps it
    // lower, the goal reply at 2.4 s since 2026-10-21), then onDone.
    // Holding the story pauses it. A tap anywhere on the slide that isn't another control skips ahead (the bubble,
    // the answer they just gave, empty space, the right tap zone; client feedback 2026-10-19: nobody should have to
    // wait it out), a ring filling around the "Weiter" button next to the bubble shows it moves on by itself. A new
    // slide drops it without onDone, a new reply in the same host replaces it. Reduced motion: no dots, no ring.
    let replyTimer = 0;
    let replyDone = null;
    let replyLeft = 0; // ms of reading left while the text is up (0 during the typing dots)
    let replyStart = 0;
    let replyBar = null;
    const dropReply = () => {
      window.clearTimeout(replyTimer);
      replyTimer = 0;
      replyDone = null;
      replyLeft = 0;
      replyBar = null;
    };
    const pauseReply = () => {
      if (!replyTimer || !replyLeft) return;
      window.clearTimeout(replyTimer);
      replyTimer = 0;
      replyLeft = Math.max(0, replyLeft - (performance.now() - replyStart));
      if (replyBar) replyBar.style.animationPlayState = 'paused';
    };
    const resumeReply = () => {
      if (replyTimer || !replyLeft) return;
      replyStart = performance.now();
      replyTimer = window.setTimeout(endReply, replyLeft);
      if (replyBar) replyBar.style.animationPlayState = '';
    };
    const endReply = () => {
      const done = replyDone;
      dropReply();
      done?.();
    };
    const face = () => {
      const avatar = root.querySelector('.vhp-story__avatar');
      if (!avatar) return null;
      const pic = avatar.cloneNode(true);
      pic.className = 'vhp-story-reply__face';
      return pic;
    };
    const reply = (host, text, { onDone, maxRead = 8000 } = {}) => {
      dropReply();
      if (!host || !text) {
        onDone?.();
        return;
      }
      host.querySelectorAll(':scope > .vhp-story-reply').forEach((el) => el.remove());
      const row = document.createElement('div');
      row.className = 'vhp-story-reply';
      row.setAttribute('role', 'status');
      const pic = face();
      if (pic) row.append(pic);
      const bubble = document.createElement('p');
      bubble.className = 'vhp-story-reply__bubble';
      // "Weiter" next to the bubble: a ring fills around it over the reading time (the autoplay ring of video
      // apps), so it says both "this moves on by itself" and "tap to go on now". The click is the slide's skip
      // (capture handler on the funnel). In the row from the start, so the measured height already counts it.
      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'vhp-story-reply__next';
      next.setAttribute('aria-label', root.querySelector('[data-vhp-story-tap="next"]')?.getAttribute('aria-label') || '');
      next.innerHTML =
        '<svg viewBox="0 0 32 32" aria-hidden="true"><circle class="vhp-story-reply__track" cx="16" cy="16" r="14.5"/>' +
        '<circle class="vhp-story-reply__ring" cx="16" cy="16" r="14.5" pathLength="1"/>' +
        '<path d="m14 10.5 5.5 5.5-5.5 5.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      row.append(bubble, next);
      replyDone = onDone || null;
      const read = Math.min(maxRead, Math.max(1800, 600 + (text.length * 1000) / (REPLY_CPS + 2)));
      const words = document.createElement('span');
      words.className = 'vhp-story-reply__text';
      words.textContent = text;
      let release = () => {};
      const speak = () => {
        // The typing bubble grows into the message inside the room the row already made, the words fade in.
        morph(bubble, () => {
          bubble.replaceChildren(words);
        });
        window.setTimeout(() => release(), 400);
        row.classList.add('is-said');
        buzz(10);
        replyLeft = read;
        replyStart = performance.now();
        replyTimer = paused ? 0 : window.setTimeout(endReply, read);
        if (motionOk()) {
          replyBar = next.querySelector('.vhp-story-reply__ring');
          replyBar.style.animationDuration = `${read}ms`;
          if (paused) replyBar.style.animationPlayState = 'paused';
          replyBar.classList.add('is-on');
        }
      };
      if (!motionOk()) {
        host.append(row);
        speak();
        return;
      }
      const dots = document.createElement('span');
      dots.className = 'vhp-story-reply__dots';
      dots.setAttribute('aria-hidden', 'true');
      dots.append(document.createElement('i'), document.createElement('i'), document.createElement('i'));
      // Measure the finished message once, so the sticker makes room in one smooth move, not twice.
      bubble.append(words);
      host.append(row);
      const full = row.offsetHeight;
      bubble.replaceChildren(dots);
      release = growIn(row, { to: full });
      replyTimer = window.setTimeout(speak, REPLY_DOTS_MS);
    };

    // A short line from Sören over the deck (first like, a high-protein like). Never blocks swiping.
    let sayEl = null;
    let sayTimer = 0;
    const say = (text, ms = 1600) => {
      if (!text || !view) return;
      ms = Math.max(ms, Math.min(5000, 600 + (text.length * 1000) / REPLY_CPS)); // long enough to read
      if (!sayEl) {
        sayEl = document.createElement('div');
        sayEl.className = 'vhp-story__say';
        sayEl.setAttribute('role', 'status');
        root.append(sayEl);
      }
      const bubble = document.createElement('p');
      bubble.textContent = text;
      const pic = face();
      sayEl.replaceChildren(...(pic ? [pic] : []), bubble);
      sayEl.classList.remove('is-on');
      void sayEl.offsetWidth;
      sayEl.classList.add('is-on');
      buzz(8);
      window.clearTimeout(sayTimer);
      sayTimer = window.setTimeout(() => sayEl?.classList.remove('is-on'), ms);
    };

    // "Gedrückt halten" (3.1): the ring ([data-vhp-hold-ring], an SVG circle) fills while pressed, three haptic
    // ticks on the way; released early it springs back (onShort(n)). Full: burst, then onDone. A keyboard or screen
    // reader click (no pointer), reduced motion and the third press build right away, so nobody is locked out.
    const holdToCommit = (btn, { ms = COMMIT_MS, onDone, onShort } = {}) => {
      if (!btn) return null;
      if (btn.vhpHold) return btn.vhpHold;
      const ring = btn.querySelector('[data-vhp-hold-ring]');
      const len = ring ? 2 * Math.PI * Number(ring.getAttribute('r') || 0) : 0;
      let from = 0;
      let loop = 0;
      let done = false;
      let pressed = false;
      let shorts = 0;
      let ticks = 0;
      const paint = (p) => {
        if (ring && len) ring.style.strokeDashoffset = String(len * (1 - p));
      };
      if (ring && len) {
        ring.style.strokeDasharray = String(len);
        paint(0);
      }
      const finish = () => {
        if (done) return;
        done = true;
        pressed = false;
        cancelAnimationFrame(loop);
        paint(1);
        btn.classList.remove('is-holding');
        btn.classList.add('is-done');
        buzz(24);
        burst(btn, { count: 34, spread: 1.2 });
        onDone?.();
      };
      const step = (now) => {
        const p = Math.min(1, (now - from) / ms);
        paint(p);
        const t = Math.floor(p * 3);
        if (t > ticks) {
          ticks = t;
          buzz(8);
        }
        if (p >= 1) finish();
        else loop = requestAnimationFrame(step);
      };
      btn.addEventListener('pointerdown', (event) => {
        if (done || pressed || event.button > 0) return;
        event.preventDefault();
        // A second short tap builds it (a plain tap is what most try first; the hint after the first says hold).
        if (!motionOk() || shorts >= 1) {
          finish();
          return;
        }
        pressed = true;
        try {
          btn.setPointerCapture(event.pointerId);
        } catch (e) {
          /* no capture: pointerup still ends it */
        }
        from = performance.now();
        ticks = 0;
        btn.classList.add('is-holding');
        loop = requestAnimationFrame(step);
      });
      const release = () => {
        if (!pressed || done) return;
        pressed = false;
        cancelAnimationFrame(loop);
        btn.classList.remove('is-holding');
        shorts += 1;
        paint(0);
        onShort?.(shorts);
      };
      btn.addEventListener('pointerup', release);
      btn.addEventListener('pointercancel', release);
      btn.addEventListener('lostpointercapture', release);
      btn.addEventListener('contextmenu', (event) => event.preventDefault());
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.detail === 0) finish();
      });
      btn.vhpHold = {
        reset: () => {
          done = false;
          pressed = false;
          shorts = 0;
          cancelAnimationFrame(loop);
          btn.classList.remove('is-done', 'is-holding');
          paint(0);
        },
      };
      return btn.vhpHold;
    };

    // The visitor touched an action on a passive slide (30-min toggle, "anpassen"): no auto-advance any more.
    const hold = () => {
      held = true;
    };

    // ---------- scratch foil (gift slide) ----------

    const foil = (canvas, { onDone }) => {
      if (!canvas || canvas.dataset.ready) return null;
      canvas.dataset.ready = '1';
      const box = canvas.getBoundingClientRect();
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.max(1, Math.round(box.width * ratio));
      canvas.height = Math.max(1, Math.round(box.height * ratio));
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        onDone();
        return null;
      }
      const w = canvas.width;
      const h = canvas.height;
      const grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0, '#c9ccd1');
      grad.addColorStop(0.45, '#eef0f2');
      grad.addColorStop(0.55, '#b8bcc2');
      grad.addColorStop(1, '#dfe2e6');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      // Fine glitter so it reads as foil, not as a grey box.
      for (let i = 0; i < (w * h) / 900; i += 1) {
        ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.5})`;
        ctx.fillRect(Math.random() * w, Math.random() * h, ratio, ratio);
      }
      ctx.fillStyle = 'rgba(28,28,26,0.62)';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `${Math.round(34 * ratio)}px system-ui, sans-serif`;
      ctx.fillText('🎁', w / 2, h / 2 - 24 * ratio);
      ctx.font = `700 ${Math.round(17 * ratio)}px "DM Sans", system-ui, sans-serif`;
      ctx.fillText(canvas.dataset.hint || '', w / 2, h / 2 + 18 * ratio);
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 44 * ratio;

      let last = null;
      let done = false;
      let moves = 0;
      const point = (event) => {
        const r = canvas.getBoundingClientRect();
        return [(event.clientX - r.left) * ratio, (event.clientY - r.top) * ratio];
      };
      const cleared = () => {
        const data = ctx.getImageData(0, 0, w, h).data;
        let clear = 0;
        let seen = 0;
        for (let i = 3; i < data.length; i += 4 * 16) {
          seen += 1;
          if (data[i] === 0) clear += 1;
        }
        return seen ? clear / seen : 1;
      };
      const reveal = () => {
        if (done) return;
        done = true;
        canvas.classList.add('is-gone');
        buzz(18);
        burst(canvas, { count: 34, spread: 1.3 });
        onDone();
      };
      canvas.addEventListener('pointerdown', (event) => {
        if (done) return;
        canvas.setPointerCapture?.(event.pointerId);
        last = point(event);
        ctx.beginPath();
        ctx.arc(last[0], last[1], 22 * ratio, 0, Math.PI * 2);
        ctx.fill();
      });
      canvas.addEventListener('pointermove', (event) => {
        if (done || !last) return;
        const p = point(event);
        ctx.beginPath();
        ctx.moveTo(last[0], last[1]);
        ctx.lineTo(p[0], p[1]);
        ctx.stroke();
        last = p;
        moves += 1;
        if (moves % 6 === 0) {
          buzz(4);
          if (cleared() > 0.55) reveal();
        }
      });
      const up = () => {
        last = null;
        if (!done && cleared() > 0.55) reveal();
      };
      canvas.addEventListener('pointerup', up);
      canvas.addEventListener('pointercancel', up);
      return { reveal };
    };

    // Holographic card (gift discount): tilts toward the finger / cursor anywhere in the story (the further from the
    // card, the less), light and sheen follow it; without a pointer for a moment it sways by itself. One eased loop
    // drives all of it, so leaving the card, the text under it or the frame never jumps. CSS reads --rx --ry --mx
    // --my (assets/vhp-planner-story.css). Called on every render of the gift slide: starts the loop again if needed.
    const holo = (el) => {
      if (!el || !motionOk()) return;
      if (el.vhpHolo) {
        el.vhpHolo.start();
        return;
      }
      const cur = { rx: 0, ry: 0, mx: 50, my: 35 };
      let aim = null;
      let lastMove = 0;
      let loop = 0;
      const t0 = performance.now();
      const IDLE_MS = 1400;
      const onMove = (event) => {
        const card = el.getBoundingClientRect();
        const frame = funnel.getBoundingClientRect();
        if (!card.width || !frame.width) return;
        const clamp = (n) => Math.max(-1, Math.min(1, n));
        const nx = clamp((event.clientX - (card.left + card.width / 2)) / (frame.width / 2));
        const ny = clamp((event.clientY - (card.top + card.height / 2)) / (frame.height / 2.5));
        aim = { rx: -ny * 16, ry: nx * 20, mx: 50 + nx * 50, my: 50 + ny * 50 };
        lastMove = performance.now();
      };
      const onLeave = () => {
        lastMove = 0;
      };
      funnel.addEventListener('pointermove', onMove);
      funnel.addEventListener('pointerdown', onMove);
      funnel.addEventListener('pointerleave', onLeave);
      const step = (now) => {
        loop = 0;
        if (!el.isConnected || !el.classList.contains('is-holo') || !el.offsetParent) return;
        const idle = !aim || now - lastMove > IDLE_MS;
        const s = (now - t0) / 1000;
        const target = idle
          ? { rx: Math.sin(s * 1.1) * 6, ry: Math.sin(s * 0.8 + 1) * 9, mx: 50 + Math.sin(s * 0.8) * 30, my: 45 + Math.cos(s * 1.1) * 25 }
          : aim;
        // Slow drift back into the sway, quick but soft follow while pointed.
        const ease = idle ? 0.04 : 0.16;
        Object.keys(cur).forEach((key) => {
          cur[key] += (target[key] - cur[key]) * ease;
        });
        el.style.setProperty('--rx', `${cur.rx.toFixed(2)}deg`);
        el.style.setProperty('--ry', `${cur.ry.toFixed(2)}deg`);
        el.style.setProperty('--mx', `${cur.mx.toFixed(1)}%`);
        el.style.setProperty('--my', `${cur.my.toFixed(1)}%`);
        loop = requestAnimationFrame(step);
      };
      el.vhpHolo = {
        start: () => {
          if (!loop) loop = requestAnimationFrame(step);
        },
      };
      el.vhpHolo.start();
    };

    // Warm a slide's clips before it opens: every clip gets a media fragment (#t=start) so the browser fetches
    // from the cut, the first two load fully. Once per clip; nothing on Save-Data / slow connections.
    const warm = (key, n = 2) => {
      if (slowNet) return;
      figs
        .filter((fig) => fig.dataset.vhpStoryFig === key)
        .forEach((fig, i) => {
          const video = fig.querySelector('video');
          if (!video || video.dataset.warm) return;
          video.dataset.warm = '1';
          const start = Number(fig.dataset.start);
          if (Number.isFinite(start) && start > 0) {
            video.querySelectorAll('source').forEach((source) => {
              if (!/#t=/.test(source.getAttribute('src') || '')) source.setAttribute('src', `${source.getAttribute('src')}#t=${start}`);
            });
          }
          video.preload = i < n ? 'auto' : 'metadata';
          try {
            video.load();
          } catch (e) {
            /* not loadable yet */
          }
        });
    };

    return { render, hold, foil, burst, react, holo, reply, say, holdToCommit, morph, warm };
  };

  window.vhpPlannerStory = { create };
})();
